import {
  ArrowRight,
  UserPlus,
  ChevronLeft,
  ChevronRight,
  Star,
  Shield,
  Clock,
  Users,
  MessageCircle,
} from 'lucide-react';
import { Logo } from '@/components/ui/custom';
import { cn } from '@/lib/utils';
import { useState, useEffect } from 'react';

interface HeroSectionProps {
  onExploreClick: () => void;
  onProfessionalSignupClick: () => void;
}

/** Copy sobre a imagem à esquerda, em painel claro (foto sem máscara escura). */
const bannerSlides = [
  {
    id: 1,
    title: 'Atendimento via WhatsApp',
    subtitle: 'Fala com um profissional de forma simples e discreta, do teu espaço.',
    image: '/images/hero/banner-1.png',
    focal: '88% 42%',
    alt: 'Pessoa em ambiente confortável a usar o telemóvel',
  },
  {
    id: 2,
    title: 'Profissionais verificados',
    subtitle: 'Psicólogos validados pela nossa equipa. Confiança e qualidade em cada perfil.',
    image: '/images/hero/banner-2.png',
    focal: '22% 48%',
    alt: 'Psicóloga profissional sorridente',
  },
  {
    id: 3,
    title: 'Mais de 500 pessoas apoiadas',
    subtitle: 'Junta-te a quem já deu o primeiro passo em direção ao bem-estar.',
    image: '/images/hero/banner-3.png',
    focal: '50% 100%',
    alt: 'Grupo de pessoas sorridentes em ambiente acolhedor',
  },
  {
    id: 4,
    title: 'Sessões desde 8.000 Kz',
    subtitle: 'Preços acessíveis para diferentes realidades e apoio ao teu ritmo.',
    image: '/images/hero/banner-4.png',
    focal: '24% 50%',
    alt: 'Pessoa sentada com expressão de tranquilidade',
  },
] as const;

const slideFraction = 100 / bannerSlides.length;

export function HeroSection({ onExploreClick, onProfessionalSignupClick }: HeroSectionProps) {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % bannerSlides.length);
    }, 5500);
    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % bannerSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + bannerSlides.length) % bannerSlides.length);
  };

  const active = bannerSlides[currentSlide];

  return (
    <section className="relative flex flex-col overflow-hidden bg-gradient-to-br from-white via-kuiva-light/45 to-white">
      <header className="kuiva-motion-hero-header relative z-20 flex min-w-0 shrink-0 items-center justify-between gap-3 border-b border-white/50 bg-white/[0.55] px-3 py-3 shadow-[0_4px_24px_rgb(15_23_42/0.04)] backdrop-blur-xl safe-area-px safe-area-pt sm:px-6 sm:py-4">
        <div className="min-w-0 shrink transition-transform duration-300 hover:scale-[1.02]">
          <Logo variant="horizontal" size="lg" className="h-12 sm:h-20 md:h-24" />
        </div>
        <button
          type="button"
          onClick={onProfessionalSignupClick}
          className="flex shrink-0 touch-manipulation items-center gap-1.5 rounded-xl border-2 border-kuiva-blue/30 bg-white/70 px-3 py-2 text-xs font-semibold text-kuiva-blue backdrop-blur-md transition-all duration-500 ease-kuiva hover:-translate-y-0.5 hover:border-kuiva-blue hover:bg-white/95 hover:shadow-md active:translate-y-0 active:scale-[0.98] sm:gap-2 sm:px-4 sm:py-2.5 sm:text-sm"
        >
          <UserPlus className="h-4 w-4 shrink-0" />
          <span className="hidden min-[400px]:inline">Sou profissional</span>
          <span className="min-[400px]:hidden">Profissional</span>
        </button>
      </header>

      <div className="relative z-10 px-3 pb-1 pt-4 sm:px-6 sm:pb-2 sm:pt-6">
        <div className="kuiva-motion-hero-card mx-auto max-w-6xl overflow-hidden rounded-2xl border border-white/55 bg-white/40 shadow-kuiva-lg backdrop-blur-md sm:rounded-3xl">
          <div className="relative h-[clamp(13.75rem,44vmin,22rem)] w-full overflow-hidden sm:h-[clamp(15rem,40vmin,24rem)] md:h-[clamp(17rem,36vmin,28rem)]">
            <div
              className="flex h-full w-full"
              style={{
                width: `${bannerSlides.length * 100}%`,
                transform: `translateX(-${currentSlide * slideFraction}%)`,
                transition: 'transform 1.05s cubic-bezier(0.22, 1, 0.36, 1)',
              }}
            >
              {bannerSlides.map((slide, slideIndex) => (
                <div
                  key={slide.id}
                  style={{ width: `${slideFraction}%` }}
                  className="relative h-full flex-shrink-0"
                >
                  <img
                    src={slide.image}
                    alt={slide.alt}
                    className={cn(
                      'absolute inset-0 h-full w-full object-cover',
                      slideIndex === currentSlide && 'kuiva-motion-ken',
                    )}
                    style={{ objectPosition: slide.focal }}
                    loading={slide.id === 1 ? 'eager' : 'lazy'}
                    decoding="async"
                    fetchPriority={slide.id === 1 ? 'high' : 'low'}
                  />
                </div>
              ))}
            </div>

            <div className="pointer-events-none absolute inset-0 z-10 flex items-center">
              <div className="relative flex h-full w-full items-center px-3 sm:px-5 md:px-6">
                <div
                  key={currentSlide}
                  className="kuiva-motion-slide-caption w-full max-w-[min(100%,19rem)] rounded-2xl border border-white/90 bg-white/92 p-4 text-left shadow-kuiva-lg ring-1 ring-kuiva-blue/[0.08] backdrop-blur-xl sm:max-w-[min(100%,23rem)] sm:rounded-3xl sm:p-5 md:max-w-[min(100%,26rem)] md:p-6"
                >
                  <p className="mb-1.5 text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-kuiva-blue/90 sm:text-xs">
                    Kuiva
                  </p>
                  <h3 className="text-balance text-lg font-semibold leading-snug tracking-tight text-foreground sm:text-xl md:text-2xl">
                    {active.title}
                  </h3>
                  <p className="mt-2 text-pretty text-sm leading-relaxed text-muted-foreground line-clamp-3 sm:mt-2.5 sm:text-base md:line-clamp-4">
                    {active.subtitle}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-center gap-2 border-t border-white/45 bg-white/30 px-4 py-3 backdrop-blur-md sm:gap-3 sm:px-6 sm:py-4">
            <div className="flex shrink-0 items-center justify-center gap-2">
              <button
                type="button"
                onClick={prevSlide}
                className="group flex h-10 w-10 items-center justify-center rounded-full border border-white/60 bg-white/50 text-kuiva-blue shadow-sm backdrop-blur-sm transition-all duration-200 hover:scale-105 hover:border-kuiva-blue/35 hover:bg-white/75 active:scale-95"
                aria-label="Slide anterior"
              >
                <ChevronLeft className="h-5 w-5 transition-transform duration-200 group-hover:-translate-x-0.5" />
              </button>
              <div className="flex items-center gap-1.5 px-1">
                {bannerSlides.map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setCurrentSlide(index)}
                    aria-label={`Ir para o slide ${index + 1}`}
                    className={cn(
                      'h-2 rounded-full transition-all duration-500 ease-out',
                      index === currentSlide
                        ? 'w-7 bg-kuiva-blue'
                        : 'w-2 bg-kuiva-blue/25 hover:scale-110 hover:bg-kuiva-blue/40',
                    )}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={nextSlide}
                className="group flex h-10 w-10 items-center justify-center rounded-full border border-white/60 bg-white/50 text-kuiva-blue shadow-sm backdrop-blur-sm transition-all duration-200 hover:scale-105 hover:border-kuiva-blue/35 hover:bg-white/75 active:scale-95"
                aria-label="Slide seguinte"
              >
                <ChevronRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-0.5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <main className="relative z-10 flex flex-col px-3 pb-3 pt-5 sm:px-6 sm:pb-6 sm:pt-6">
        <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
          <div className="kuiva-motion-blob-a absolute -right-20 -top-20 h-96 w-96 rounded-full bg-kuiva-blue/10 blur-3xl" />
          <div className="kuiva-motion-blob-b absolute top-1/3 -left-20 h-72 w-72 rounded-full bg-kuiva-green/10 blur-3xl" />
          <div className="kuiva-motion-blob-c absolute bottom-20 right-10 h-64 w-64 rounded-full bg-kuiva-accent/10 blur-3xl" />
        </div>
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 md:gap-5 lg:gap-6">
          <div className="flex flex-col justify-center py-1 lg:py-3">
            <div className="mb-3 inline-flex animate-fade-in items-center gap-2 rounded-full border border-white/55 bg-white/55 px-3 py-1.5 shadow-md backdrop-blur-md transition-shadow duration-300 hover:border-white/70 hover:bg-white/70 hover:shadow-lg sm:mb-4 sm:px-4 sm:py-2">
              <span className="flex items-center gap-1">
                <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                <span className="text-sm font-semibold">4,9</span>
              </span>
              <span className="text-sm text-muted-foreground">|</span>
              <span className="text-sm text-muted-foreground">Mais de 500 avaliações</span>
            </div>

            <h1 className="mb-3 animate-fade-in-up break-words text-[clamp(1.6rem,4vw+0.65rem,3.25rem)] font-extrabold leading-[1.12] text-foreground sm:mb-4 sm:text-5xl lg:text-6xl">
              Não precisas de enfrentar tudo{' '}
              <span className="text-kuiva-gradient-motion">sozinho.</span>
            </h1>

            <p className="animate-fade-in-up stagger-1 mb-4 max-w-lg text-sm text-muted-foreground sm:mb-5 sm:text-base md:text-lg">
            O KUIVA é uma plataforma digital 100% online que te conecta a psicólogos e terapeutas qualificados, de forma acessível e segura. Acreditamos que todos merecem ser ouvidos e ter acesso ao apoio emocional necessário para enfrentar os desafios do dia-a-dia.
            </p>

            <div className="animate-fade-in-up stagger-2 mb-4 flex flex-col gap-2.5 sm:mb-5 sm:flex-row sm:gap-3">
              <button
                type="button"
                onClick={onExploreClick}
                className="group btn-kuiva flex w-full min-[400px]:w-auto touch-manipulation items-center justify-center gap-2 px-6 py-3.5 text-base sm:px-8 sm:py-4 sm:text-lg"
              >
                Encontrar apoio
                <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>

            <div className="animate-fade-in-up stagger-3 flex flex-wrap gap-x-5 gap-y-3 sm:gap-x-6">
              <div className="flex items-center gap-2 transition-transform duration-200 hover:translate-y-[-2px]">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-kuiva-blue/10 transition-transform duration-200 hover:scale-105">
                  <Users className="h-5 w-5 text-kuiva-blue" />
                </div>
                <div>
                  <p className="font-bold text-foreground">12</p>
                  <p className="text-xs text-muted-foreground">Profissionais</p>
                </div>
              </div>
              <div className="flex items-center gap-2 transition-transform duration-200 hover:translate-y-[-2px]">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-kuiva-green/10 transition-transform duration-200 hover:scale-105">
                  <Shield className="h-5 w-5 text-kuiva-green" />
                </div>
                <div>
                  <p className="font-bold text-foreground">100%</p>
                  <p className="text-xs text-muted-foreground">Verificados</p>
                </div>
              </div>
              <div className="flex items-center gap-2 transition-transform duration-200 hover:translate-y-[-2px]">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-kuiva-accent/10 transition-transform duration-200 hover:scale-105">
                  <Clock className="h-5 w-5 text-kuiva-accent" />
                </div>
                <div>
                  <p className="font-bold text-foreground">24 h</p>
                  <p className="text-xs text-muted-foreground">Disponível</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <div className="relative z-10 shrink-0 animate-fade-in border-t border-white/60 bg-white/75 backdrop-blur-xl">
        <div className="mx-auto max-w-6xl px-3 py-3 safe-area-px sm:px-6 sm:py-4">
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 sm:gap-x-8 sm:gap-y-2">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100">
                <Shield className="h-4 w-4 text-green-700" />
              </div>
              <span className="text-center text-xs font-semibold text-slate-800 sm:text-sm">100% confidencial</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-kuiva-blue/20">
                <MessageCircle className="h-4 w-4 text-sky-700" />
              </div>
              <span className="text-center text-xs font-semibold text-slate-800 sm:text-sm">WhatsApp direto</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-kuiva-green/25">
                <Star className="h-4 w-4 text-emerald-800" />
              </div>
              <span className="max-w-[10rem] text-center text-xs font-semibold text-slate-800 sm:max-w-none sm:text-sm">
                Profissionais verificados
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
